import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import {
  Link as RouterLink,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';

import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { CryptoCompletedModal } from '@/Screens/Dashboard/Flows/common/CryptoCompletedModal';
import { DepositFlowAction, DepositFlowState } from '@/Screens/Dashboard/Flows/DepositFlow/reducer';
import { Locale } from '@/types/locale';
import { getCurrencyName } from '@/utility/formatAmount';
import { OutletReducer } from '@/utility/reducers';

export const DepositCompleted: React.FC = (): React.ReactElement => {
  const tr = useTranslator();
  const [searchParams] = useSearchParams();
  const crypto = searchParams.get('crypto');

  // For crypto deposits, show simplified completion modal (no outlet context needed)
  if (crypto) {
    return (
      <CryptoCompletedModal
        title={tr('CRYPTO_DEPOSIT_COMPLETED_TITLE')}
        description={tr('CRYPTO_DEPOSIT_COMPLETED_DESCRIPTION')}
      />
    );
  }

  // For non-crypto deposits, render the full completion screen
  return <FiatDepositCompleted />;
};

const FiatDepositCompleted: React.FC = (): React.ReactElement => {
  const [state] = useOutletContext<OutletReducer<DepositFlowState, DepositFlowAction>>();

  const { toAmount, toCurrency } = state;
  const navigate = useNavigate();

  const locale = React.useContext<Locale>(LocaleContext);
  const formatter = useLocalizedFormatter({
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  const tr = useTranslator();

  const onClose = React.useCallback((): void => {
    navigate('/home', { replace: true });
  }, [navigate]);

  const displayUsdcAsUsd = useDisplayUsdcAsUsd();

  const currencyName = React.useMemo((): string => {
    return getCurrencyName(locale, toCurrency, displayUsdcAsUsd);
  }, [toCurrency, locale, displayUsdcAsUsd]);

  const textVariableFont = React.useMemo((): number => {
    const length = currencyName.length;

    if (length <= 10) {
      return 2.5;
    }

    if (length >= 20) {
      return 1;
    }

    // Divided by 16 to get the font size in rem
    return Math.round((-1.25 * length + 41) / 16);
  }, [currencyName]);

  return (
    <ApplicationModal
      title={tr('YOUR_FUNDS_HAVE_BEEN_ADDED')}
      description={tr('CHECK_YOUR_PAYMENT_HISTORY_TO_TRACK_STATUS')}
      onClose={onClose}>
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Box sx={styles.amountContainer}>
          <Typography fontSize={12} fontWeight={400} lineHeight="20px" color="custom.LightGray">
            {tr('CALIZA_WALLET_BALANCE')}
          </Typography>
          <Box sx={styles.priceCurrencyPairContainer}>
            <Typography
              component="span"
              fontSize={40}
              fontWeight={500}
              lineHeight="48px"
              color="text.primary">
              ${formatter.format(toAmount ?? 0)}
            </Typography>
            <Typography
              title={currencyName}
              sx={styles.currency}
              component="span"
              fontSize={`${textVariableFont}rem`}
              fontWeight={500}
              lineHeight="48px"
              color="custom.LightGray">
              {' '}
              {currencyName}
            </Typography>
          </Box>
        </Box>
        <Box width="100%" mt={6}>
          <Button component={RouterLink} to="/home/transfer" variant="dark" fullWidth={true}>
            {tr('TRANSFER_FUNDS')}
          </Button>
          <Box sx={styles.cancelButton}>
            <Link onClick={onClose}>
              <Typography fontSize={16} fontWeight={400} lineHeight="24px">
                {tr('BACK_TO_DASHBOARD')}
              </Typography>
            </Link>
          </Box>
        </Box>
      </Box>
    </ApplicationModal>
  );
};

const styles: Record<string, SystemStyleObject> = {
  priceCurrencyPairContainer: {
    maxWidth: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 1,
  },
  currency: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cancelButton: {
    mt: 3,
    display: 'flex',
    justifyContent: 'center',
  },
  amountContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 0.5,
    width: 513,
    height: 120,
    textAlign: 'center',
    borderRadius: 4,
    bgcolor: 'background.paper',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
    mt: 1,
    px: 2,
  },
};
